import { TableName } from '../../../tables'

export const MAX_FILE_SIZE_MB = 5

const MAGIC_BYTES: Record<string, number[]> = {
    'application/pdf': [0x25, 0x50, 0x44, 0x46],
    'image/jpeg': [0xFF, 0xD8, 0xFF],
    'image/png': [0x89, 0x50, 0x4E, 0x47],
}

const detectMimeFromBytes = (bytes: Uint8Array): string | null => {
    for (const [mime, signature] of Object.entries(MAGIC_BYTES)) {
        if (signature.every((byte, i) => bytes[i] === byte)) return mime
    }
    return null
}

export const validateFileMimeType = async (file: File, allowedMimes: string[]): Promise<boolean> => {
    if (allowedMimes.length === 0) return true
    const buffer = await file.slice(0, 8).arrayBuffer()
    const bytes = new Uint8Array(buffer)
    const detected = detectMimeFromBytes(bytes)
    if (detected === null) return true
    const normalized = allowedMimes.map(m => (m === 'image/jpg' ? 'image/jpeg' : m))
    return normalized.includes(detected)
}

export const compressImage = async (file: File, maxSizeMB = MAX_FILE_SIZE_MB): Promise<File | null> => {
    const maxSizeBytes = maxSizeMB * 1024 * 1024
    if (!file.type.startsWith('image/') || file.size <= maxSizeBytes) return null

    return new Promise((resolve) => {
        const img = new Image()
        const objectUrl = URL.createObjectURL(file)

        img.onload = () => {
            URL.revokeObjectURL(objectUrl)
            const canvas = document.createElement('canvas')
            canvas.width = img.naturalWidth
            canvas.height = img.naturalHeight
            const ctx = canvas.getContext('2d')
            if (!ctx) { resolve(null); return }
            ctx.drawImage(img, 0, 0)

            const tryQuality = (qualities: number[]) => {
                const [quality, ...rest] = qualities
                if (quality === undefined) { resolve(null); return }
                canvas.toBlob(blob => {
                    if (!blob) { resolve(null); return }
                    const compressed = new File([blob], file.name, { type: 'image/jpeg' })
                    if (compressed.size <= maxSizeBytes) {
                        resolve(compressed)
                    } else {
                        tryQuality(rest)
                    }
                }, 'image/jpeg', quality)
            }
            tryQuality([0.7, 0.5, 0.3])
        }
        img.onerror = () => { URL.revokeObjectURL(objectUrl); resolve(null) }
        img.src = objectUrl
    })
}

export const fileNameFromUrl = (fileUrl?: string) => fileUrl?.split('/').pop()

export const fileSubPath = (
    table: TableName,
    id: string,
    fileName: string
) => {
    if (table === TableName.COMPANIES) {
        return fileName
    }

    const partsMap = new Map<TableName, string>()
        .set(TableName.CATEGORIES, 'category')
        .set(TableName.CREDIT_PAYMENTS, 'creditPayment')
        .set(TableName.EXPENSES, 'expense')
        .set(TableName.HOARD_ORDERS, 'hoardOrder')
        .set(TableName.PAYMENT_ORDERS, 'paymentOrder')
        .set(TableName.PRODUCTS, 'product')
        .set(TableName.SALE_ORDERS, 'saleOrder')
        .set(TableName.TICKETS, 'ticket')
    
    return `${partsMap.get(table)}/${id}/${fileName}`
}

export const toLocalUrl = async (url: string): Promise<string> => {
    const res = await fetch(url)
    const blob = await res.blob()
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(reader.result as string)
        reader.onerror = reject
        reader.readAsDataURL(blob)
    })
}
